import React, { Fragment } from 'react'
import { connect } from 'react-redux'
import { browserHistory } from 'react-router'
import styles from './ExpandedRoutePreview.scss'
import { troutesMessages } from '../../shared/intlMessages'
import { SmartExport } from 'common/SmartExport'
import { MapAndGraph } from 'common/MapAndGraph'
import { Distance, Elevation, Grade } from 'common/units'
import { Loadable } from 'common/Loadable'
import { CoreLink } from 'common/CoreLink'
import { SHARE_DIALOG, ShareDialog } from 'common/ShareDialog'
import { CTA } from 'common/styleguide/CTA'
import { CircleSpinner } from 'common/spinners'
import { urls } from 'lib/urls'
import { j } from 'lib/textUtils'
import { Msg, fm, commonNouns, commonVerbs, commonMetrics } from 'lib/localization'
import { routeActions, routeSelectors } from 'data/resources/routes'
import _ from 'lodash'
import moment from 'moment'
import { injectIntl } from 'react-intl'

export class ExpandedRoutePreview extends React.PureComponent {
  constructor (props) {
    super(props)

    this.state = {
      dialog: null,
      routeName: props.route?.name ?? '(unnamed)',
      editing: false,
      updatePending: false
    }
  }

  // server disallows empty route in favor of "(unnamed)", do the same in the routeNameInput
  componentDidUpdate (prevProps, prevState) {
    if (!prevState.routeName && prevState.editing && !this.state.editing) {
      this.setState({ routeName: this.props.route.name })
    }
  }

  hideDialog = () => (
    this.setState({ dialog: null })
  )

  showDialog = (dialog) => (
    this.setState({ dialog: dialog })
  )

  toggleEditing = () => {
    this.setState({ editing: !this.state.editing })
  }

  onRouteNameChange = (e) => {
    this.setState({ routeName: e.target.value })
  }

  saveRouteName = () => {
    const { routeName, updatePending } = this.state
    if (updatePending) {
      return null
    }
    const { updateRoute, route, updateName } = this.props
    this.setState({ updatePending: true }, () => {
      updateRoute(route.id, { route: { name: routeName } })
        .then(r => this.setState({ editing: false, updatePending: false, routeName: r.action.payload.route.name }))
        .then(updateName(routeName)) // TODO remove once search is in redux
    })
  }

  render () {
    const { dialog, routeName, editing, updatePending } = this.state
    const { route, metricUnits, intl } = this.props
    const { metrics } = route

    return (
      <div className={styles.ExpandedRoutePreview}>
        <div className={styles.left}>
          {editing
            ? <div className={styles.editingRouteName}>
              <input value={routeName || ''} onChange={this.onRouteNameChange} className={styles.routeNameInput} />
              <span className={styles.saveButton}>
                <CTA secondary margins={false} disabled={updatePending} onClick={this.saveRouteName}>
                  {updatePending ? <CircleSpinner size={14} /> : fm(intl, commonVerbs.save)}
                </CTA>
              </span>
            </div>
            : <h3><span className={styles.routeName} onClick={this.toggleEditing}>{route.name}</span></h3>}
          <div className={styles.metrics}>
            <div className={styles.metricsSection}>
              <MetricsRow label={fm(intl, troutesMessages.created)} value={moment(route.createdAt).format('MMM DD, \'YY, HH:mmA')} />
              <MetricsRow label={fm(intl, troutesMessages.startsIn)} value={j([route.locality, route.administrativeArea]) || '-'} />
              <MetricsRow label={fm(intl, commonMetrics.distance)} M={Distance} short value={route.distance} />
              <div className={styles.metricsRow}>
                <div className={styles.metricsCol}><Msg m={commonMetrics.elevation} />:</div>
                <div className={styles.metricsCol}>+<Elevation short value={route.elevationGain} />/-<Elevation short value={route.elevationLoss} /></div>
              </div>
              {metrics && metrics.grade && <MetricsRow label={fm(intl, commonMetrics.maxGrade)} M={Grade} short value={metrics.grade.max} />}
            </div>

            {route.description && <div className={styles.description}>{route.description}</div>}
          </div>

          <div className={styles.export}>
            <SmartExport
              route={route}
              sendToDevice={() => browserHistory.push(`/routes/${route.id}/send_to_device`)} />
          </div>
          <div className={styles.links}>
            <div className={styles.linkWrap}>
              <CoreLink href={urls.routes.show(route.id, route.privacyCode)}><Msg m={troutesMessages.viewFullDetails} /></CoreLink>
            </div>
            <div className={styles.linkWrap}>
              <a onClick={() => this.showDialog(SHARE_DIALOG)}><Msg m={commonVerbs.share} /></a>
              {dialog === SHARE_DIALOG &&
                <ShareDialog onRequestClose={this.hideDialog}
                  objectType='route'
                  objectId={route.id}
                  objectTitle={route.name}
                  privacyCode={route.privacyCode}
                  objectUrl={urls.routes.show(route.id, route.privacyCode)} />}
            </div>
          </div>
        </div>

        <div className={styles.right}>
          {route && <MapAndGraph
            height='500'
            troute={route}
            metricUnits={metricUnits}
            mapSettings={{ showPOIs: true }}
          />}
        </div>
      </div>
    )
  }
}

const MetricsRow = ({ M, label, value, title, ...metricProps }) => {
  if (M !== undefined) {
    return (value !== null) && <div className={styles.metricsRow}>
      <div className={styles.metricsCol}>{label}:</div>
      <div className={styles.metricsCol}><span data-tip={!!title} title={title}><M value={value} {...metricProps} /></span></div>
    </div>
  } else {
    return value && <div className={styles.metricsRow}>
      <div className={styles.metricsCol}>{label}:</div>
      <div className={styles.metricsCol}><Fragment>{value}</Fragment></div>
    </div>
  }
}

const loadInitialResources = _.memoize(({ troute, loadRoute }) => {
  return loadRoute(troute.id)
})

const _LoadableExpandedRoutePreview = (props) => {
  return <Loadable
    {...props}
    component={ExpandedRoutePreview}
    loadingText={fm(props.intl, troutesMessages.loading, {
      type: _.capitalize(fm(props.intl, commonNouns.routes, { count: 1 }))
    })}
    resourceLoaded={() => loadInitialResources(props)} />
}

const mapStateToProps = (state, ownProps) => {
  return {
    route: routeSelectors.find(state, ownProps.troute.id)
  }
}

const mapDispatchToProps = {
  loadRoute: routeActions.read,
  updateRoute: routeActions.update
}

export const LoadableExpandedRoutePreview = connect(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(_LoadableExpandedRoutePreview))
